import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import type { BandwidthPeriodOverTimeTrend } from './bandwidthDashboardTypes';
import { linearRegression } from './bandwidthChartMath';
import { fmtBwDecimal } from './bandwidthFormat';

export interface BandwidthHoursAndCountOverTimeChartProps {
  title: string;
  subtitle?: string;
  periodOverTimeTrend: BandwidthPeriodOverTimeTrend;
  emptyHint?: string;
}

const HOURS_COLOR = 'rgb(37, 99, 235)';
const COUNT_COLOR = 'rgb(100, 116, 139)';
const TREND_COLOR = 'rgb(15, 23, 42)';

const BandwidthHoursAndCountOverTimeChart: React.FC<BandwidthHoursAndCountOverTimeChartProps> = ({
  title,
  subtitle,
  periodOverTimeTrend,
  emptyHint,
}) => {
  const { granularity, points } = periodOverTimeTrend;

  const chartData = useMemo(() => {
    const labels = points.map((p) => p.periodLabel);
    const hours = points.map((p) => p.totalHours);
    const counts = points.map((p) => p.activityCount);
    const xIdx = points.map((_, i) => i);
    const hReg = linearRegression(xIdx, hours);
    const hoursTrend = xIdx.map((i) => hReg.slope * i + hReg.intercept);
    const cReg = linearRegression(xIdx, counts);
    const countTrend = xIdx.map((i) => cReg.slope * i + cReg.intercept);

    return { labels, hours, counts, hoursTrend, countTrend };
  }, [points]);

  const xAxisTitle = granularity === 'day' ? 'Day' : 'Month';

  if (points.length === 0) {
    return (
      <KpiChartPanel
        title={title}
        titleClassName="!pl-0"
        className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
        flexFill
        bodyClassName="px-3 pb-4 pt-4"
      >
        {subtitle ? <p className="mb-2 text-xs text-slate-500">{subtitle}</p> : null}
        <p className="py-12 text-center text-sm text-slate-500">
          {emptyHint ?? 'No data for the selected filters.'}
        </p>
      </KpiChartPanel>
    );
  }

  const { labels, hours, counts, hoursTrend, countTrend } = chartData;
  const pointRadius = granularity === 'day' ? 2 : 3;
  const pointHoverRadius = granularity === 'day' ? 4 : 5;

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      className="h-full min-h-0 max-h-[min(28rem,70vh)] lg:max-h-[440px]"
      flexFill
      bodyClassName="px-3 pb-4 pt-3"
    >
      {subtitle ? <p className="mb-1 text-xs leading-snug text-slate-500">{subtitle}</p> : null}
      <div className="relative h-[260px] w-full min-h-0 flex-1 sm:h-[280px]">
        <Line
          data={{
            labels,
            datasets: [
              {
                label: 'Total time (hrs)',
                data: hours,
                yAxisID: 'y',
                borderColor: HOURS_COLOR,
                backgroundColor: 'rgba(37, 99, 235, 0.08)',
                fill: true,
                tension: 0.2,
                pointRadius,
                pointHoverRadius,
                borderWidth: 2,
                order: 2,
              },
              {
                label: 'Hours trend',
                data: hoursTrend,
                yAxisID: 'y',
                borderColor: TREND_COLOR,
                borderDash: [6, 4],
                fill: false,
                tension: 0,
                pointRadius: 0,
                borderWidth: 1.5,
                order: 3,
              },
              {
                label: 'Activity count',
                data: counts,
                yAxisID: 'y1',
                borderColor: COUNT_COLOR,
                backgroundColor: 'transparent',
                fill: false,
                tension: 0.2,
                pointRadius: granularity === 'day' ? 1.5 : 2,
                borderWidth: 1.5,
                order: 1,
              },
              {
                label: 'Count trend',
                data: countTrend,
                yAxisID: 'y1',
                borderColor: TREND_COLOR,
                borderDash: [4, 3],
                fill: false,
                tension: 0,
                pointRadius: 0,
                borderWidth: 1,
                order: 4,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: {
                display: true,
                position: 'bottom',
                labels: {
                  boxWidth: 10,
                  font: { size: 10 },
                  color: '#475569',
                },
              },
              tooltip: {
                callbacks: {
                  title: (items) => {
                    const idx = items[0]?.dataIndex;
                    if (idx == null || idx < 0 || idx >= points.length) return '';
                    const p = points[idx];
                    if (granularity === 'day') {
                      const [y, m, d] = p.periodStart.split('-').map((x) => parseInt(x, 10));
                      if (y && m && d) {
                        const date = new Date(Date.UTC(y, m - 1, d));
                        return date.toLocaleString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          timeZone: 'UTC',
                        });
                      }
                    }
                    return p.periodLabel;
                  },
                  label: (ctx) => {
                    const v = ctx.parsed.y;
                    if (v == null || Number.isNaN(v)) return '';
                    if (ctx.dataset.label?.includes('count') || ctx.dataset.yAxisID === 'y1') {
                      return `${ctx.dataset.label}: ${Math.round(v).toLocaleString()}`;
                    }
                    return `${ctx.dataset.label}: ${fmtBwDecimal(v)}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { color: 'rgba(148, 163, 184, 0.2)' },
                title: { display: true, text: xAxisTitle, color: '#64748b', font: { size: 11 } },
                ticks: {
                  font: { size: 10 },
                  maxRotation: granularity === 'day' ? 45 : 45,
                  minRotation: granularity === 'day' ? 45 : 0,
                  autoSkip: true,
                  maxTicksLimit: granularity === 'day' ? 31 : undefined,
                  color: '#64748b',
                },
              },
              y: {
                position: 'left',
                beginAtZero: true,
                grid: { color: 'rgba(148, 163, 184, 0.25)' },
                title: { display: true, text: 'Hours', color: '#64748b', font: { size: 11 } },
                ticks: { font: { size: 10 }, color: '#64748b' },
              },
              y1: {
                position: 'right',
                beginAtZero: true,
                grid: { drawOnChartArea: false },
                title: { display: true, text: 'Activity count', color: '#64748b', font: { size: 11 } },
                ticks: { font: { size: 10 }, color: '#64748b' },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthHoursAndCountOverTimeChart;
